// The `/claim?token=…` page (see `features/athlete-access.md`): where an athlete
// or guardian turns an emailed invitation into an account.
//
// Two properties this screen exists to hold:
//
//   * The token is redeemed only on an explicit click, never on page load. Like a
//     magic link it is single-use, and JS-executing email security scanners
//     (Safe Links, Mimecast…) fetch and render links — auto-claiming would let
//     one burn the invitation before the recipient ever sees it.
//   * A password is optional. Without one the account is passwordless and signs
//     in through the emailed-link flow instead, which is the friendlier default
//     for a parent who would rather not invent another password.
//
// It renders outside `AuthProvider` (forked in `main.tsx`), so it holds its own
// small piece of session handling: on success it stores the returned bearer token
// and reloads into the app, which then routes to the athlete surface.

import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { PASSWORD_MIN } from '../domain';
import {
  claimAthleteInvite,
  clearClaimTokenFromUrl,
  setToken,
} from '../data/authClient';
import { useTranslation } from '../state/I18nContext';
import { BrandLogo } from './BrandLogo';
import { LanguageSelector } from './LanguageSelector';

interface ClaimScreenProps {
  /** The one-time token from the emailed link. */
  token: string;
}

export function ClaimScreen({ token }: ClaimScreenProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [wantsPassword, setWantsPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordValid = !wantsPassword || password.length >= PASSWORD_MIN;

  const submit = async () => {
    if (submitting || !passwordValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await claimAthleteInvite(
        token,
        wantsPassword && password.length > 0 ? password : undefined,
      );
      // Store the session, drop the spent token from the address bar, then reload
      // into the app so `AuthProvider` starts from a clean, authenticated state.
      setToken(result.token);
      clearClaimTokenFromUrl();
      window.location.replace('/');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('athlete.claim.failed'));
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 440, border: 1, borderColor: 'divider' }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <BrandLogo height={30} />
            <Typography variant="h5" sx={{ flexGrow: 1 }}>
              {t('athlete.claim.title')}
            </Typography>
            <LanguageSelector />
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('athlete.claim.body')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2}>
            {/* A password is opt-in; the default is the emailed-link sign-in. */}
            {wantsPassword ? (
              <TextField
                fullWidth
                autoFocus
                type="password"
                label={t('athlete.claim.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={password.length > 0 && password.length < PASSWORD_MIN}
                helperText={t('athlete.claim.passwordHelp', { min: PASSWORD_MIN })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void submit();
                }}
              />
            ) : (
              <Collapse in appear={false}>
                <Typography variant="body2" color="text.secondary">
                  {t('athlete.claim.passwordlessHint')}
                </Typography>
              </Collapse>
            )}

            <Button
              size="large"
              variant="contained"
              startIcon={submitting ? <CircularProgress size={18} /> : <HowToRegIcon />}
              disabled={submitting || !passwordValid}
              onClick={() => void submit()}
            >
              {t('athlete.claim.action')}
            </Button>

            <Button
              size="small"
              onClick={() => {
                setWantsPassword((current) => !current);
                setPassword('');
              }}
            >
              {t(
                wantsPassword
                  ? 'athlete.claim.useEmailLink'
                  : 'athlete.claim.setPassword',
              )}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
